

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useAppConfig } from '@ct-react/core';
import { useLocaleContext } from '@ct-react/locale';
import { CmsPageContent, extractCmsContent } from '@shared/graphql';
import { createContext, useContext, useMemo } from 'react';

const CMS_NEWS_GQL_DATA = gql`
  query NewsPostCmsPage($newsTemplateId: UUID!) {
    cmsPages(templateId: $newsTemplateId) {
      data
      id
      publishedAt
    }
  }
`;

export type NewsContextType = {
  loading: boolean;
  newsContent: NewsEntry[] | undefined;
};

type NewsCmsPageContent<T = any> = {
  id: string;
  publishedAt: string;
  data: T;
};

type NewsContent = CmsPageContent;

export type NewsEntry = NewsCmsPageContent<NewsContent>;

export const NewsContext = createContext<NewsContextType | undefined>(undefined);

export const useNewsContext = () => {
  const context = useContext(NewsContext);

  if (!context) {
    throw new Error('useNewsContext must be used within NewsProvider');
  }

  return context;
};

const NewsProvider = ({ children }: { children: React.ReactNode }) => {
  const { locale } = useLocaleContext();
  const { options: { cmsTemplates: { news: newsTemplateId } } } = useAppConfig();

  const { data: newsData, loading } = useQuery<{ cmsPages: NewsCmsPageContent[] }>(CMS_NEWS_GQL_DATA, {
    ssr: true,
    variables: { newsTemplateId }
  });

  const newsContent = useMemo<NewsEntry[] | undefined>(() => {
    if (!newsData?.cmsPages?.length) return undefined;

    return newsData.cmsPages.map((page: NewsCmsPageContent) => {
      return {...page, data: extractCmsContent(page.data, locale)} as NewsEntry
    });
  }, [newsData, locale]);

  return (
    <NewsContext.Provider value={{ loading, newsContent }}>
      {children}
    </NewsContext.Provider>
  );
};

export default NewsProvider;
